import { departmentForNotification } from './departments'
/**
 * Native platform services using Capacitor
 * Provides access to device features on iOS/Android
 */

import { Capacitor } from '@capacitor/core';
import { sendRequest } from '../../core/api/send.js'

// Check if running on native platform
export const isNative = () => Capacitor.isNativePlatform();
export const platform = () => Capacitor.getPlatform(); // 'web', 'ios', 'android'

/**
 * Initialize native plugins on app startup
 */
export async function initNativePlugins() {
  if (!isNative()) return;
  
  try {
    // Initialize status bar
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: '#0a0f1c' });
    
    // Hide splash screen after a delay
    const { SplashScreen } = await import('@capacitor/splash-screen');
    setTimeout(() => {
      SplashScreen.hide();
    }, 500);
    
    console.log(`Native plugins initialized on ${platform()}`);
  } catch (error) {
    console.error('Error initializing native plugins:', error);
  }
}

/**
 * Push Notifications (iOS/Android only)
 *
 * Call on every sign-in, not once per launch: a shared station iPad changes
 * hands without restarting the app, and each member who signs in has to take
 * the device's token over (the server keeps one owner per token). Sign-out
 * hands it back with unregisterPushNotifications().
 *
 * The listeners are attached once, before register(): the token can arrive as
 * soon as register() is called, and a listener added afterwards could miss it.
 *
 * @param {object} options
 * @param {function} options.getToken - Function to get auth token
 * @param {function} options.navigate - React Router navigate function
 */
let pushListenersAdded = false
let pushDeviceToken = null
let pushGetAuthToken = null
let pushNavigate = null

const authHeaders = () => {
  const authToken = pushGetAuthToken ? pushGetAuthToken() : null
  return authToken ? { 'Authorization': `Bearer ${authToken}` } : {}
}

export async function initPushNotifications({ getToken, navigate } = {}) {
  if (!isNative()) return null;
  pushGetAuthToken = getToken || null
  pushNavigate = navigate || null

  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    const permission = await PushNotifications.requestPermissions();
    if (permission.receive !== 'granted') {
      console.log('Push notification permission denied');
      return null;
    }

    if (!pushListenersAdded) {
      pushListenersAdded = true

      // Token from Apple/Google -- send it to the server for this member
      PushNotifications.addListener('registration', async (token) => {
        pushDeviceToken = token.value
        try {
          await sendRequest('/api/push/register', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({ token: token.value, platform: platform() })
          });
        } catch (err) {
          console.error('Failed to register push token with backend:', err);
        }
      });

      PushNotifications.addListener('registrationError', (error) => {
        console.error('Push registration error:', error);
      });

      PushNotifications.addListener('pushNotificationReceived', (notification) => {
        console.log('Push received:', notification?.title);
      });

      // Tapping a notification opens what it is about -- in the department
      // that sent it, which may not be the one open (see departments.js).
      PushNotifications.addListener('pushNotificationActionPerformed', async (action) => {
        const data = action.notification?.data || {};
        const deepLink = typeof data.deepLink === 'string' && data.deepLink.startsWith('/') ? data.deepLink : null;
        const other = departmentForNotification(data.department);
        if (other) {
          const { switchToDepartment } = await import('./departmentSwitch');
          await switchToDepartment(other.url, { deepLink });
          return;
        }
        if (deepLink && pushNavigate) pushNavigate(deepLink);
      });
    }

    // Each call fires 'registration' again, which re-sends the token under
    // whoever is signed in now.
    await PushNotifications.register();
    return true;
  } catch (error) {
    console.error('Error initializing push notifications:', error);
    return null;
  }
}

/**
 * Stop this device receiving the signed-out member's notifications. Call while
 * still signed in (it needs the auth token). Never throws or delays sign-out
 * for long.
 */
/** This device's push token, once registration has returned it. */
export const getPushDeviceToken = () => pushDeviceToken

export async function unregisterPushNotifications() {
  if (!isNative() || !pushDeviceToken) return
  const token = pushDeviceToken
  try {
    await Promise.race([
      sendRequest('/api/push/unregister', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ token })
      }),
      new Promise((resolve) => setTimeout(resolve, 3000))
    ])
  } catch (err) {
    console.error('Failed to unregister push token:', err);
  }
}

/**
 * Camera - for incident photos
 */
export async function takePhoto() {
  try {
    const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');
    
    const image = await Camera.getPhoto({
      quality: 80,
      allowEditing: false,
      resultType: CameraResultType.Base64,
      source: CameraSource.Camera,
    });
    
    return {
      base64: image.base64String,
      format: image.format,
    };
  } catch (error) {
    if (error.message !== 'User cancelled photos app') {
      console.error('Camera error:', error);
    }
    return null;
  }
}

/**
 * Pick photo from gallery
 */
export async function pickPhoto() {
  try {
    const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');
    
    const image = await Camera.getPhoto({
      quality: 80,
      allowEditing: false,
      resultType: CameraResultType.Base64,
      source: CameraSource.Photos,
    });
    
    return {
      base64: image.base64String,
      format: image.format,
    };
  } catch (error) {
    if (error.message !== 'User cancelled photos app') {
      console.error('Photo picker error:', error);
    }
    return null;
  }
}

/**
 * Geolocation - for incident location
 */
export async function getCurrentLocation() {
  try {
    const { Geolocation } = await import('@capacitor/geolocation');
    
    // Check permission
    const permission = await Geolocation.checkPermissions();
    if (permission.location !== 'granted') {
      const requested = await Geolocation.requestPermissions();
      if (requested.location !== 'granted') {
        throw new Error('Location permission denied');
      }
    }
    
    const position = await Geolocation.getCurrentPosition({
      enableHighAccuracy: true,
      timeout: 10000,
    });
    
    return {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
      timestamp: position.timestamp,
    };
  } catch (error) {
    console.error('Geolocation error:', error);
    return null;
  }
}

/**
 * Local Notifications - for reminders
 */
export async function scheduleLocalNotification(options) {
  if (!isNative()) return null;
  
  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    
    // Check permission
    const permission = await LocalNotifications.checkPermissions();
    if (permission.display !== 'granted') {
      const requested = await LocalNotifications.requestPermissions();
      if (requested.display !== 'granted') {
        return null;
      }
    }
    
    const notificationId = Date.now();
    
    await LocalNotifications.schedule({
      notifications: [
        {
          id: notificationId,
          title: options.title,
          body: options.body,
          schedule: options.at ? { at: new Date(options.at) } : undefined,
          extra: options.data,
        },
      ],
    });
    
    return notificationId;
  } catch (error) {
    console.error('Local notification error:', error);
    return null;
  }
}

/**
 * Managed App Config — reads pinAuthSecret from MDM-deployed configuration
 * Returns the secret string on managed iOS devices, null otherwise
 */
export async function getManagedSecret() {
  if (!isNative() || platform() !== 'ios') return null;

  try {
    const { registerPlugin } = await import('@capacitor/core');
    const ManagedConfig = registerPlugin('ManagedConfig');
    const result = await ManagedConfig.getSecret();
    return result?.secret || null;
  } catch (error) {
    console.log('Managed config not available:', error.message);
    return null;
  }
}

// ── Biometric Authentication ─────────────────────────────────────────────────

/**
 * Check if biometric hardware is available on this device
 * Returns { isAvailable, biometryType } or { isAvailable: false } on web
 */
export async function isBiometricAvailable() {
  if (!isNative()) return { isAvailable: false }

  try {
    const { NativeBiometric } = await import('@capgo/capacitor-native-biometric')
    const result = await NativeBiometric.isAvailable()
    return { isAvailable: result.isAvailable, biometryType: result.biometryType }
  } catch (error) {
    console.log('Biometric not available:', error.message)
    return { isAvailable: false }
  }
}

/**
 * Show biometric prompt and verify the user
 * @param {string} reason - Displayed to the user
 * @returns {boolean} true if verified
 */
export async function verifyBiometric(reason = 'Verify your identity') {
  if (!isNative()) return false

  try {
    const { NativeBiometric } = await import('@capgo/capacitor-native-biometric')
    await NativeBiometric.verifyIdentity({ reason, title: 'Scenelight' })
    return true
  } catch (error) {
    console.log('Biometric verification failed:', error.message)
    return false
  }
}

/**
 * Keychain entry for the active department's biometric sign-in. Each
 * department has its own, so switching departments keeps Face ID working for
 * all of them. Before departments could be switched there was one entry,
 * 'app.scenelight.portal'; getBiometricCredential() moves it on first use.
 */
const LEGACY_BIOMETRIC_SERVER = 'app.scenelight.portal'
function biometricServer() {
  try {
    const url = localStorage.getItem('scenelightServerUrl')
    return url ? `${LEGACY_BIOMETRIC_SERVER}.${new URL(url).host}` : LEGACY_BIOMETRIC_SERVER
  } catch {
    return LEGACY_BIOMETRIC_SERVER
  }
}

/**
 * Store a biometric credential in the device secure enclave
 * @param {string} username - User identifier
 * @param {string} token - Biometric token to store
 */
export async function storeBiometricCredential(username, token) {
  if (!isNative()) return false

  try {
    const { NativeBiometric } = await import('@capgo/capacitor-native-biometric')
    await NativeBiometric.setCredentials({
      username,
      password: token,
      server: biometricServer()
    })
    return true
  } catch (error) {
    console.error('Failed to store biometric credential:', error)
    return false
  }
}

/**
 * Retrieve stored biometric credential from secure enclave
 * @returns {{ username: string, token: string } | null}
 */
export async function getBiometricCredential() {
  if (!isNative()) return null

  try {
    const { NativeBiometric } = await import('@capgo/capacitor-native-biometric')
    const server = biometricServer()
    let cred = await NativeBiometric.getCredentials({ server }).catch(() => null)
    if (!cred?.password && server !== LEGACY_BIOMETRIC_SERVER) {
      // The one entry from before per-department storage belongs to the
      // department that was active then, which is still the active one.
      const legacy = await NativeBiometric.getCredentials({ server: LEGACY_BIOMETRIC_SERVER }).catch(() => null)
      if (legacy?.username && legacy?.password) {
        await NativeBiometric.setCredentials({ username: legacy.username, password: legacy.password, server })
        await NativeBiometric.deleteCredentials({ server: LEGACY_BIOMETRIC_SERVER }).catch(() => {})
        cred = legacy
      }
    }
    if (cred?.username && cred?.password) {
      return { username: cred.username, token: cred.password }
    }
    return null
  } catch (error) {
    console.log('No biometric credential stored:', error.message)
    return null
  }
}

/**
 * Check if a biometric credential is saved (without retrieving it)
 */
export async function hasBiometricCredential() {
  if (!isNative()) return false

  try {
    const cred = await getBiometricCredential()
    return !!cred
  } catch {
    return false
  }
}

/**
 * Delete stored biometric credential from secure enclave
 */
export async function deleteBiometricCredential() {
  if (!isNative()) return

  try {
    const { NativeBiometric } = await import('@capgo/capacitor-native-biometric')
    await NativeBiometric.deleteCredentials({ server: biometricServer() })
  } catch (error) {
    console.log('No biometric credential to delete:', error.message)
  }
}

/**
 * Get a stable device ID (persisted in localStorage)
 * Used to scope biometric tokens to a specific device
 */
export function getBiometricDeviceId() {
  const KEY = 'biometricDeviceId'
  let id = localStorage.getItem(KEY)
  if (!id) {
    id = crypto.randomUUID()
    localStorage.setItem(KEY, id)
  }
  return id
}

/**
 * App info
 */
export async function getAppInfo() {
  if (!isNative()) {
    return {
      platform: 'web',
      version: '1.0.0',
    };
  }
  
  try {
    const { App } = await import('@capacitor/app');
    const info = await App.getInfo();
    
    return {
      platform: platform(),
      name: info.name,
      version: info.version,
      build: info.build,
    };
  } catch (error) {
    console.error('App info error:', error);
    return {
      platform: platform(),
      version: '1.0.0',
    };
  }
}
